import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Paperclip, FileText, X } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED = ["pdf", "doc", "docx", "jpg", "jpeg", "png", "webp", "txt", "rtf", "odt"];

const TABS = [
  { key: "employer", label: "Hire Talent", hash: "hire" },
  { key: "candidate", label: "Find a Job", hash: "find-job" },
  { key: "partner", label: "Become a Partner", hash: "partner" },
];

const input =
  "w-full bg-transparent border-b border-[#e4e4e7] py-3 font-body text-base text-[#0a0a0a] placeholder:text-[#a1a1aa] focus:outline-none focus:border-[#ff3b30] transition-colors duration-300";

const FileField = ({ label, files, setFiles, testid }) => {
  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    const valid = [];
    for (const f of picked) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`${f.name}: unsupported type`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: exceeds 10MB`); continue; }
      valid.push(f);
    }
    setFiles((p) => [...p, ...valid].slice(0, 5));
    e.target.value = "";
  };
  return (
    <div>
      <label htmlFor={testid} className="flex items-center gap-3 border border-dashed border-[#e4e4e7] hover:border-[#ff3b30] cursor-pointer px-4 py-4 transition-colors duration-300">
        <Paperclip size={18} className="text-[#ff3b30] shrink-0" />
        <span className="font-body text-sm text-[#52525b]">
          {label}
          <span className="block text-xs text-[#a1a1aa]">PDF, DOC, DOCX, JPG, PNG · up to 5 files · 10MB each</span>
        </span>
      </label>
      <input id={testid} data-testid={testid} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt,.rtf,.odt" className="hidden" onChange={add} />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 bg-[#f5f5f4] border border-[#e4e4e7] px-3 py-2">
              <FileText size={15} className="text-[#52525b] shrink-0" />
              <span className="font-body text-sm text-[#0a0a0a] truncate flex-1">{f.name}</span>
              <button type="button" onClick={() => setFiles((p) => p.filter((_, x) => x !== i))} className="text-[#a1a1aa] hover:text-[#ff3b30]" aria-label="Remove">
                <X size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const uploadAll = async (files) => {
  const attachments = [];
  for (const f of files) {
    const fd = new FormData();
    fd.append("file", f);
    const up = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    attachments.push(up.data);
  }
  return attachments;
};

const SubmitBtn = ({ loading, label }) => (
  <button type="submit" disabled={loading} data-testid="form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#ff3b30] hover:bg-[#ff5248] disabled:opacity-60 text-white font-head uppercase tracking-wide px-8 py-4 font-600 transition-colors duration-300">
    {loading ? (<>Sending <Loader2 size={18} className="animate-spin" /></>) : label}
  </button>
);

const EmployerForm = () => {
  const [f, setF] = useState({ company_name: "", country: "", contact_person: "", email: "", phone: "", position_required: "", number_of_workers: "", salary_range: "", accommodation: "", start_date: "", message: "" });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name || !f.contact_person || !f.email || !f.position_required) { toast.error("Please fill company, contact person, email and position."); return; }
    setLoading(true);
    try {
      const attachments = await uploadAll(files);
      const res = await axios.post(`${API}/employer-request`, { ...f, attachments });
      toast.success(res.data.message); setF({ company_name: "", country: "", contact_person: "", email: "", phone: "", position_required: "", number_of_workers: "", salary_range: "", accommodation: "", start_date: "", message: "" }); setFiles([]);
    } catch (err) { console.error(err); toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} data-testid="employer-form" className="space-y-6">
      <p className="font-body text-[#52525b]">Tell us your manpower requirement and our recruitment team will review your vacancy.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input data-testid="emp-company" className={input} placeholder="Company name *" value={f.company_name} onChange={s("company_name")} />
        <input data-testid="emp-country" className={input} placeholder="Country" value={f.country} onChange={s("country")} />
        <input data-testid="emp-contact" className={input} placeholder="Contact person *" value={f.contact_person} onChange={s("contact_person")} />
        <input data-testid="emp-email" type="email" className={input} placeholder="Email *" value={f.email} onChange={s("email")} />
        <input data-testid="emp-phone" className={input} placeholder="WhatsApp / Phone" value={f.phone} onChange={s("phone")} />
        <input data-testid="emp-position" className={input} placeholder="Position required *" value={f.position_required} onChange={s("position_required")} />
        <input data-testid="emp-workers" className={input} placeholder="Number of workers" value={f.number_of_workers} onChange={s("number_of_workers")} />
        <input data-testid="emp-salary" className={input} placeholder="Salary range" value={f.salary_range} onChange={s("salary_range")} />
        <input data-testid="emp-accom" className={input} placeholder="Accommodation" value={f.accommodation} onChange={s("accommodation")} />
        <input data-testid="emp-start" className={input} placeholder="Start date" value={f.start_date} onChange={s("start_date")} />
      </div>
      <textarea data-testid="emp-message" rows={3} className={`${input} resize-none`} placeholder="Additional notes" value={f.message} onChange={s("message")} />
      <FileField label="Upload job description (optional)" files={files} setFiles={setFiles} testid="emp-file" />
      <SubmitBtn loading={loading} label="Request workforce" />
    </form>
  );
};

const CandidateForm = () => {
  const [f, setF] = useState({ full_name: "", nationality: "", current_country: "", profession: "", experience: "", preferred_country: "", phone: "", email: "" });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.profession || !f.phone) { toast.error("Please fill name, profession and phone."); return; }
    setLoading(true);
    try {
      const attachments = await uploadAll(files);
      const payload = { ...f, attachments }; if (!payload.email) delete payload.email;
      const res = await axios.post(`${API}/candidate-submit`, payload);
      toast.success(res.data.message); setF({ full_name: "", nationality: "", current_country: "", profession: "", experience: "", preferred_country: "", phone: "", email: "" }); setFiles([]);
    } catch (err) { console.error(err); toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} data-testid="candidate-form" className="space-y-6">
      <p className="font-body text-[#52525b]">Looking for work in Europe? Share your details and CV and we'll match you with verified roles.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input data-testid="cand-name" className={input} placeholder="Full name *" value={f.full_name} onChange={s("full_name")} />
        <input data-testid="cand-nationality" className={input} placeholder="Nationality" value={f.nationality} onChange={s("nationality")} />
        <input data-testid="cand-current" className={input} placeholder="Current country" value={f.current_country} onChange={s("current_country")} />
        <input data-testid="cand-profession" className={input} placeholder="Profession *" value={f.profession} onChange={s("profession")} />
        <input data-testid="cand-experience" className={input} placeholder="Experience (e.g. 5 years)" value={f.experience} onChange={s("experience")} />
        <input data-testid="cand-preferred" className={input} placeholder="Preferred country" value={f.preferred_country} onChange={s("preferred_country")} />
        <input data-testid="cand-phone" className={input} placeholder="WhatsApp / Phone *" value={f.phone} onChange={s("phone")} />
        <input data-testid="cand-email" type="email" className={input} placeholder="Email (optional)" value={f.email} onChange={s("email")} />
      </div>
      <FileField label="Upload your CV" files={files} setFiles={setFiles} testid="cand-file" />
      <SubmitBtn loading={loading} label="Submit your CV" />
    </form>
  );
};

const PartnerForm = () => {
  const [f, setF] = useState({ company_name: "", country: "", contact_person: "", email: "", phone: "", message: "" });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name || !f.contact_person || !f.email || !f.message) { toast.error("Please fill company, contact, email and message."); return; }
    setLoading(true);
    try {
      const attachments = await uploadAll(files);
      const res = await axios.post(`${API}/partner-request`, { ...f, attachments });
      toast.success(res.data.message); setF({ company_name: "", country: "", contact_person: "", email: "", phone: "", message: "" }); setFiles([]);
    } catch (err) { console.error(err); toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} data-testid="partner-form" className="space-y-6">
      <p className="font-body text-[#52525b]">Agencies and B2B partners — let's build trusted recruitment cooperation across Europe.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input data-testid="par-company" className={input} placeholder="Company / Agency *" value={f.company_name} onChange={s("company_name")} />
        <input data-testid="par-country" className={input} placeholder="Country" value={f.country} onChange={s("country")} />
        <input data-testid="par-contact" className={input} placeholder="Contact person *" value={f.contact_person} onChange={s("contact_person")} />
        <input data-testid="par-email" type="email" className={input} placeholder="Email *" value={f.email} onChange={s("email")} />
        <input data-testid="par-phone" className={input} placeholder="WhatsApp / Phone" value={f.phone} onChange={s("phone")} />
      </div>
      <textarea data-testid="par-message" rows={4} className={`${input} resize-none`} placeholder="Tell us about your agency and how you'd like to cooperate *" value={f.message} onChange={s("message")} />
      <FileField label="Company profile (optional)" files={files} setFiles={setFiles} testid="par-file" />
      <SubmitBtn loading={loading} label="Become a partner" />
    </form>
  );
};

export const GetStarted = () => {
  const [tab, setTab] = useState("employer");

  useEffect(() => {
    const apply = () => {
      const h = window.location.hash.replace("#", "");
      const match = TABS.find((t) => t.hash === h);
      if (match) setTab(match.key);
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  return (
    <section id="get-started" data-testid="get-started-section" className="py-24 md:py-32 px-6 md:px-12">
      {/* scroll anchors for CTA hashes */}
      <span id="hire" className="block -mt-24 pt-24" aria-hidden="true" />
      <span id="find-job" className="block -mt-24 pt-24" aria-hidden="true" />
      <span id="partner" className="block -mt-24 pt-24" aria-hidden="true" />

      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <p className="font-body text-sm tracking-[0.25em] uppercase text-[#ff3b30] mb-6">
            Get started
          </p>
          <h2 className="font-head uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl mb-10">
            Let's talk.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-2 mb-10 border-b border-[#e4e4e7]" data-testid="get-started-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => { setTab(t.key); history.replaceState(null, "", `#${t.hash}`); }}
                className={`font-head uppercase tracking-wide text-sm px-5 py-4 -mb-px border-b-2 transition-colors duration-300 ${
                  tab === t.key ? "border-[#ff3b30] text-[#0a0a0a]" : "border-transparent text-[#a1a1aa] hover:text-[#0a0a0a]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} key={tab}>
          {tab === "employer" && <EmployerForm />}
          {tab === "candidate" && <CandidateForm />}
          {tab === "partner" && <PartnerForm />}
        </Reveal>
      </div>
    </section>
  );
};
